import styled from '@emotion/styled';
import ShowCase from 'aquilla/core/molecules/ShowCase';
import {
  convertTitleToSlug,
  getAuthorName,
  getPlaylistThumbnail,
  LANGUAGE_MAP
} from '@cont/Content/FreeLesson/utils';
import EducatorPlaylistCard from 'aquilla/core/organisms/EducatorPlaylistCard';
import { mediaQuery, Spacings } from '@styles/index';
import Image from '@base/FutureImage';
import getTopicGroupAssetObj from '@cont/Syllabus/constants/getTopicGroupAssetObj';
import pushRoute from '@utils/pushRoute';
import { useRouter } from 'next/router';
import { segment } from '@actions/analytics';
import {
  EDUCATOR_PROFILE,
  PLAYLIST_CARD_CLICKED,
  SPECIAL_CLASS_BROWSE_PAGE
} from '@cont/Content/FreeLesson/constants/events';
import { SEE_ALL_CLICKED } from '@constants/segment';

const Container = styled.div`
  grid-column: span 18;
  margin-top: ${({ $isEducator }) => {
    return $isEducator ? `${Spacings.SPACING_10B}` : `${Spacings.SPACING_6B}`;
  }};
  padding-bottom: ${({ $isEducator }) => {
    return $isEducator ? 0 : `${Spacings.SPACING_6B}`;
  }};
  ${mediaQuery.uptoMobile} {
    margin: ${({ $isEducator }) => {
      return $isEducator
        ? `${Spacings.SPACING_10B} ${Spacings.SPACING_0B} 0`
        : `${Spacings.SPACING_8B} ${Spacings.SPACING_0B}`;
    }};
    padding-bottom: ${({ $isEducator }) => {
      return $isEducator ? `${Spacings.SPACING_6B}` : `${Spacings.SPACING_0B}`;
    }};
  }
  .hideControls {
    button {
      visibility: hidden;
    }
  }
`;

export const StyledEducatorPlaylistCard = styled(EducatorPlaylistCard)`
  max-height: 282px;
  width: 272px;
  max-width: 272px;
  .aquilla-educator-playlist-educator-thumbnail-wrapper {
    height: 155px;
    width: 240px;
    > div:first-child {
      height: 155px;
    }
  }
  .aquilla-educator-playlist-details-wrapper {
    gap: 8px;
    .aquilla-educator-playlist-info {
      h6 {
        -webkit-line-clamp: 2;
      }
    }
  }
  ${mediaQuery.uptoMobile} {
    max-width: 272px;
    max-height: 282px;
  }
`;

const EduThumbWrapper = styled.div`
  position: relative;
  justify-content: center;
`;

const EduImage = styled(Image)`
  object-fit: contain;
`;

const ImageWrapper = styled.div`
  position: absolute;
  width: 170px;
  height: 155px;
  bottom: -${Spacings.SPACING_1B};

  left: 50%;
  transform: translate(-50%, 0);
  max-width: 90%;
  max-height: 100%;
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 240px;
  height: 155px;
`;

const Thumbnail = styled(Image)`
  object-fit: cover;
  width: 100%;
`;

export const EduThumbnail = ({ url, topicName }) => {
  const thumbnail = getPlaylistThumbnail(topicName);
  return (
    <EduThumbWrapper>
      <ThumbnailWrapper>
        <Thumbnail
          src={thumbnail}
          width={170}
          height={155}
          alt="edu-thumbnail"
          priority
        />
      </ThumbnailWrapper>
      <ImageWrapper>
        <EduImage src={url} width={170} height={155} alt="edu-image" priority />
      </ImageWrapper>
    </EduThumbWrapper>
  );
};
export const getLessonString = (lessonsCount) => {
  if (!lessonsCount) return null;
  if (lessonsCount > 1) return `${lessonsCount} lessons`;
  return `${lessonsCount} lesson`;
};

export const getUrl = (playListData = {}) => {
  const slug = convertTitleToSlug(playListData.name) || '-';
  return {
    href: `/curated-classes/${slug}/${playListData?.uid}`
  };
};
const CuratedPlayList = ({ block = {}, isEducator, username, educatorUID }) => {
  const { query } = useRouter();
  const { goalUID, goalSlug } = query;
  const label = block?.label || block?.metaData?.title;
  const description =
    block?.extraBlockInfo?.subLabel || block?.metaData?.subtitle;
  const { data = [] } = block;
  const handleSeeALLOnClassPage = () => {
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/curated-classes',
      `/goal/${goalSlug}/${goalUID}/curated-classes`
    );
  };
  const dataLength = data?.length;
  const handleSeeALLOnAuthorPage = () => {
    segment.track(SEE_ALL_CLICKED, {
      last_primary_source_section: 'Learn with playlists'
    });
    pushRoute('/[authorID]/curated-classes', `/@${username}/curated-classes`);
  };
  const extraProps = {};
  if (dataLength > 4) {
    extraProps.onSeeAll = isEducator
      ? handleSeeALLOnAuthorPage
      : handleSeeALLOnClassPage;
  }
  if (dataLength < 4) {
    extraProps.className = 'hideControls';
  }

  const handleCardClick = (playlistData) => {
    const author = playlistData?.authors?.[0] || {};
    const authorFullName = getAuthorName(author);
    const uid = author?.uid || educatorUID;
    segment.track(PLAYLIST_CARD_CLICKED, {
      current_screen: isEducator ? EDUCATOR_PROFILE : SPECIAL_CLASS_BROWSE_PAGE,
      goal_uid: query?.goalUID ?? 'NA',
      is_new_user: 'NA',
      playlist_name: playlistData?.name ?? 'NA',
      playlist_uid: playlistData?.uid ?? 'NA',
      educator_uid: uid ?? 'NA',
      educator_name: authorFullName,
      last_primary_source_section: 'Learn with playlists'
    });
  };

  return (
    <Container $isEducator={isEducator}>
      <ShowCase
        itemsPerSlide={3}
        title={label || 'Learn with playlists'}
        description={description || 'Structured, topic-wise lessons'}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...extraProps}
      >
        {data?.map((playlistData) => {
          const author = playlistData?.authors?.[0] || {};
          const { color } = getTopicGroupAssetObj(playlistData?.topicGroupName);
          const authorFullName = getAuthorName(author);
          const { href } = getUrl(playlistData);
          return (
            <StyledEducatorPlaylistCard
              onClick={() => handleCardClick(playlistData)}
              key={playlistData.uid}
              educatorName={authorFullName}
              title={playlistData.name}
              topicColor={color}
              topicName={playlistData.topicGroupName}
              lessons={getLessonString(playlistData.lessonsCount)}
              langLabel={
                LANGUAGE_MAP[playlistData.language]?.languageText || 'EN'
              }
              thumbnail={() => (
                <EduThumbnail
                  url={author.avatarV2 || author.oldAvatar}
                  color={color}
                  topicName={playlistData.topicGroupName}
                />
              )}
              href={href}
              educatorLink={`/@${author.username}`}
            />
          );
        })}
      </ShowCase>
    </Container>
  );
};

export default CuratedPlayList;
